import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { ReceiptLong, Refresh as RefreshIcon } from "@mui/icons-material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import DatePick from "../../Components/DatePick";
import MainText from "../../Components/MainText";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../config/permissionUtils";
import { cashService } from "../../services";
import { onCashSessionChanged } from "../../services/http/cashEvents";
import {
  buildEmptySessionDetail,
  calculateAdditionalMovementTotals,
  calculateClosureSummary,
  calculateSalesSessionMetrics,
  formatDateTime,
  getSessionNet,
  getSessionStatusColor,
  getSessionStatusLabel,
  normalizeSessionDetail,
  toArray,
  toMoney,
  toSignedMoney,
} from "./cashSessionShared";
import {
  CashSessionMovementsTable,
  ChannelSummaryCard,
  MetricCard,
} from "./components/CashSessionPanels";

function toApiDate(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function sessionNetColor(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed === 0) return "text.primary";
  return parsed < 0 ? "error.main" : "success.main";
}

const getSaleTypeLabel = (sale) =>
  String(sale?.sale_type || "").toUpperCase() === "GROOMING" ? "Servicio grooming" : "Bien/producto";

function saleNumberFromMovement(movement) {
  const text = String(movement?.description || "");
  const match = text.match(/(?:venta|sale|ticket)\s*#?\s*([A-Za-z0-9-]+)/i);
  return match?.[1] || "";
}

function findSaleForMovement(movement, sales) {
  if (!movement || movement.reason_type !== "SALE") return null;
  const movementSaleNumber = saleNumberFromMovement(movement);
  return toArray(sales).find(
    (sale) =>
      String(sale.id) === String(movementSaleNumber) ||
      String(sale.sale_number || "").toLowerCase() === String(movementSaleNumber).toLowerCase() ||
      String(movement.description || "").includes(String(sale.sale_number || "___never___"))
  ) || null;
}

function getMovementTypeLabel(movement, sales) {
  if (movement?.reason_type === "SALE") {
    return getSaleTypeLabel(findSaleForMovement(movement, sales));
  }
  return movement?.movement_type === "OUT" ? "Egreso" : "Ingreso";
}

function MovementDetailDialog({ open, onClose, movement, sale }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Detalle de movimiento</DialogTitle>
      <DialogContent dividers>
        {!movement ? (
          <Typography color="text.secondary">Selecciona un movimiento.</Typography>
        ) : (
          <Stack spacing={2}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Grid container spacing={1.2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Fecha/hora</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatDateTime(movement.occurred_at || movement.created_at)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Tipo</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{getMovementTypeLabel(movement, sale ? [sale] : [])}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Metodo/canal</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{sale?.primary_payment_label || movement.payment_channel || "-"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Monto</Typography>
                    <Typography sx={{ fontWeight: 900 }}>{toSignedMoney(Number(movement.amount || 0) * (movement.movement_type === "OUT" ? -1 : 1))}</Typography>
                  </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {movement.description || "Sin descripcion"}
                </Typography>
              </CardContent>
            </Card>

            {sale ? (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack spacing={1.4}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography sx={{ fontWeight: 900 }}>{sale.sale_number || `Venta #${sale.id}`}</Typography>
                          <Chip size="small" label={getSaleTypeLabel(sale)} variant="outlined" color={getSaleTypeLabel(sale) === "Servicio grooming" ? "info" : "default"} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Cliente: {sale.customer_display || "CF"} - Ticket: {sale.sale_number || sale.id} - {formatDateTime(sale.completed_at || sale.paid_at || sale.created_at)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Vendedor: {sale.created_by_name || sale.finalized_by_name || "-"} - Cobrador: {sale.cashier_name || sale.paid_by_name || sale.finalized_by_name || "-"}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 950, fontSize: 18 }}>{toMoney(sale.total)}</Typography>
                    </Stack>

                    <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                      {toArray(sale.payments).map((payment) => (
                        <Chip
                          key={payment.id || `${payment.payment_method_name}-${payment.amount}`}
                          size="small"
                          variant="outlined"
                          label={`${payment.payment_method_name || payment.payment_method_code || "Pago"}: ${toMoney(payment.amount)}`}
                        />
                      ))}
                    </Stack>

                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                            <TableCell align="right">Unitario</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {toArray(sale.items).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.product_description || item.description_snapshot || item.service_name || "Item"}</TableCell>
                              <TableCell>{String(item.item_category || "").toUpperCase() === "GROOMING" ? "Servicio grooming" : "Bien/producto"}</TableCell>
                              <TableCell align="right">{item.qty_presentation || item.quantity || 0}</TableCell>
                              <TableCell align="right">{toMoney(item.unit_price)}</TableCell>
                              <TableCell align="right">{toMoney(item.line_total || item.total)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="info">Movimiento sin venta vinculada. Revisa razon, canal y descripcion del movimiento.</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function CashSessionCajeroPage() {
  const { currentBranch, isSuperuser, permissions = [] } = useAuth();
  const branchId = currentBranch?.id || null;
  const today = useMemo(() => new Date(), []);

  const canViewPage = hasPermission(permissions, "cash_view", isSuperuser);

  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(buildEmptySessionDetail);
  const [sessionSales, setSessionSales] = useState([]);
  const [movementDetail, setMovementDetail] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingSales, setLoadingSales] = useState(false);
  const [filters, setFilters] = useState({
    date_from: today,
    date_to: today,
  });

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadSessions = useCallback(async () => {
    if (!branchId || !canViewPage) {
      setSessions([]);
      setSelectedSessionId(null);
      return;
    }

    setLoadingSessions(true);
    try {
      const params = {
        branch_id: branchId,
        date_from: toApiDate(filters.date_from) || undefined,
        date_to: toApiDate(filters.date_to) || undefined,
      };
      const payload = await cashService.listSessions(params);
      const rows = toArray(payload);
      setSessions(rows);
    } catch {
      setSessions([]);
      setSelectedSessionId(null);
      showSnack("No se pudo cargar el listado de sesiones.", "error");
    } finally {
      setLoadingSessions(false);
    }
  }, [branchId, canViewPage, filters.date_from, filters.date_to, showSnack]);

  const loadSessionDetailAndSales = useCallback(
    async (sessionId) => {
      if (!branchId || !sessionId) {
        setSessionDetail(buildEmptySessionDetail());
        setSessionSales([]);
        return;
      }

      setLoadingDetail(true);
      let normalizedDetail = null;
      try {
        const detailPayload = await cashService.getSession(sessionId, { branch_id: branchId });
        normalizedDetail = normalizeSessionDetail(detailPayload);
        setSessionDetail(normalizedDetail);
        setSessionSales(toArray(normalizedDetail?.sales));
      } catch {
        setSessionDetail(buildEmptySessionDetail());
        setSessionSales([]);
        showSnack("No se pudo cargar el detalle de la sesion seleccionada.", "error");
        setLoadingDetail(false);
        setLoadingSales(false);
        return;
      } finally {
        setLoadingDetail(false);
      }

      setLoadingSales(false);
    },
    [branchId, showSnack]
  );

  const refreshPage = useCallback(async () => {
    await loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    refreshPage();
  }, [refreshPage]);

  useEffect(() => {
    if (sessions.length === 0) {
      setSelectedSessionId(null);
      return;
    }
    setSelectedSessionId((prev) => {
      const hasCurrent = sessions.some((session) => Number(session.id) === Number(prev));
      return hasCurrent ? prev : sessions[0].id;
    });
  }, [sessions]);

  useEffect(() => {
    loadSessionDetailAndSales(selectedSessionId);
  }, [loadSessionDetailAndSales, selectedSessionId]);

  useEffect(() => {
    const unsubscribe = onCashSessionChanged((payload) => {
      const payloadBranchId = payload?.branchId ?? null;
      if (!branchId || (payloadBranchId && payloadBranchId !== branchId)) {
        return;
      }
      refreshPage();
    });

    return unsubscribe;
  }, [branchId, refreshPage]);

  const selectedSession = sessionDetail?.session || null;
  const selectedSummary = sessionDetail?.summary || buildEmptySessionDetail().summary;
  const selectedMovements = useMemo(() => toArray(sessionDetail?.movements), [sessionDetail?.movements]);
  const movementDetailSale = findSaleForMovement(movementDetail, sessionSales);
  const movementTotals = useMemo(() => calculateAdditionalMovementTotals(selectedMovements), [selectedMovements]);
  const salesMetrics = useMemo(() => calculateSalesSessionMetrics(sessionSales), [sessionSales]);
  const closureSummary = useMemo(
    () =>
      calculateClosureSummary({
        summary: selectedSummary,
        session: selectedSession,
        salesMetrics,
        movementTotals,
      }),
    [movementTotals, salesMetrics, selectedSession, selectedSummary]
  );

  const paymentMethodEntries = useMemo(
    () => Object.entries(salesMetrics.paymentsByMethod),
    [salesMetrics.paymentsByMethod]
  );

  if (!canViewPage) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permiso para ver la caja operativa.
        </Typography>
      </Box>
    );
  }

  if (!branchId) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sucursal no seleccionada
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecciona una sucursal para consultar sesiones de caja.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <MainText icon={ReceiptLong} title="Mi caja" subtitle="Sesiones propias por rango y detalle operativo" />
        <Tooltip title="Refrescar">
          <IconButton onClick={refreshPage}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
        <DatePick
          name="date_from"
          label="Desde"
          value={filters.date_from}
          onChange={(name, value) => setFilters((prev) => ({ ...prev, [name]: value }))}
          size="small"
          allowHistoricalDates
          maxWidth={220}
          minWidth={180}
        />
        <DatePick
          name="date_to"
          label="Hasta"
          value={filters.date_to}
          onChange={(name, value) => setFilters((prev) => ({ ...prev, [name]: value }))}
          size="small"
          allowHistoricalDates
          maxWidth={220}
          minWidth={180}
        />
      </Stack>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
        <Box sx={{ width: { xs: "100%", lg: 360 }, flexShrink: 0 }}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
            <CardContent sx={{ p: 1.5 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Sesiones
                </Typography>
                <Box sx={{ maxHeight: { xs: "auto", lg: "calc(100vh - 320px)" }, overflowY: "auto", pr: 0.5 }}>
                  <Stack spacing={1}>
                    {loadingSessions ? (
                      <Typography variant="body2" color="text.secondary">
                        Cargando sesiones...
                      </Typography>
                    ) : sessions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No hay sesiones para el rango seleccionado.
                      </Typography>
                    ) : (
                      sessions.map((session) => {
                        const isSelected = Number(session.id) === Number(selectedSessionId);
                        const netValue = getSessionNet(session?.summary || {});
                        return (
                          <Paper
                            key={session.id}
                            variant="outlined"
                            onClick={() => setSelectedSessionId(session.id)}
                            sx={{
                              p: 1.25,
                              borderColor: isSelected ? "primary.main" : "divider",
                              bgcolor: isSelected ? "rgba(16, 23, 60, 0.04)" : "background.paper",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                              "&:hover": {
                                borderColor: "primary.light",
                                boxShadow: "0 10px 24px rgba(16, 23, 60, 0.06)",
                              },
                            }}
                          >
                            <Stack spacing={0.5}>
                              <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {session?.cash_register?.name || session?.cash_register?.code || `Caja ${session.cash_register_id}`}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={getSessionStatusLabel(session?.status)}
                                  color={getSessionStatusColor(session?.status)}
                                  variant={String(session?.status || "").toUpperCase() === "OPEN" ? "filled" : "outlined"}
                                />
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                Apertura: {formatDateTime(session?.opened_at)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Cierre: {formatDateTime(session?.closed_at)}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: sessionNetColor(netValue) }}
                              >
                                Neto: {toSignedMoney(netValue)}
                              </Typography>
                            </Stack>
                          </Paper>
                        );
                      })
                    )}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              {!selectedSessionId ? (
                <Typography variant="body2" color="text.secondary">
                  Selecciona una sesion para ver el detalle.
                </Typography>
              ) : loadingDetail ? (
                <Typography variant="body2" color="text.secondary">
                  Cargando detalle...
                </Typography>
              ) : selectedSession ? (
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="h6">Sesion #{selectedSession.id}</Typography>
                      <Chip
                        size="small"
                        label={getSessionStatusLabel(selectedSession.status)}
                        color={getSessionStatusColor(selectedSession.status)}
                        variant={String(selectedSession.status || "").toUpperCase() === "OPEN" ? "filled" : "outlined"}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {selectedSession?.cash_register?.name || "Caja"} - Apertura {formatDateTime(selectedSession.opened_at)}
                    </Typography>
                  </Box>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} flexWrap="wrap">
                    <MetricCard label="Ventas" value={String(salesMetrics.salesCount)} tooltip="Cantidad de ventas cobradas durante la sesion." />
                    <MetricCard label="Productos vendidos" value={String(salesMetrics.productsSold)} tooltip="Cantidad total de unidades vendidas." />
                    <MetricCard label="Ventas cobradas" value={toMoney(salesMetrics.collectedTotal)} tooltip="Total de ventas efectivamente pagadas durante la sesion." />
                    <MetricCard label="Egresos" value={toMoney(movementTotals.outflow)} tooltip="Monto total de salidas registradas en la sesion." />
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} flexWrap="wrap">
                    <MetricCard label="Ingresos adicionales" value={toMoney(movementTotals.inflow)} tooltip="Movimientos positivos distintos de ventas." />
                    <MetricCard label="Movimientos netos" value={toSignedMoney(movementTotals.net)} tooltip="Ingresos adicionales menos egresos." />
                    <MetricCard label="Apertura" value={toMoney(selectedSummary.opening_float)} tooltip="Monto inicial con el que se abrio la sesion." />
                  </Stack>

                  <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25}>
                    <ChannelSummaryCard
                      title="Efectivo"
                      backgroundColor="#F4FCF8"
                      expected={selectedSummary.expected_cash}
                      counted={selectedSummary.counted_cash}
                      difference={selectedSummary.difference_cash}
                      isSessionOpen={String(selectedSession?.status || "").toUpperCase() === "OPEN"}
                    />
                    <ChannelSummaryCard
                      title="Tarjeta"
                      backgroundColor="#EFF6FD"
                      expected={selectedSummary.expected_card}
                      counted={selectedSummary.counted_card}
                      difference={selectedSummary.difference_card}
                      isSessionOpen={String(selectedSession?.status || "").toUpperCase() === "OPEN"}
                    />
                    <ChannelSummaryCard
                      title="Transferencia"
                      backgroundColor="#F4F3FE"
                      expected={selectedSummary.expected_transfer}
                      counted={selectedSummary.counted_transfer}
                      difference={selectedSummary.difference_transfer}
                      isSessionOpen={String(selectedSession?.status || "").toUpperCase() === "OPEN"}
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} flexWrap="wrap">
                    <MetricCard label="Esperado al cierre" value={toMoney(closureSummary.expectedTotal)} tooltip="Apertura + ingresos + ventas - egresos." />
                    <MetricCard label="Contado cajero" value={closureSummary.countedTotal == null ? "-" : toMoney(closureSummary.countedTotal)} tooltip="Total declarado por el cajero al cerrar." />
                    <MetricCard label="Diferencia" value={closureSummary.difference == null ? "-" : toSignedMoney(closureSummary.difference)} tooltip="Contado cajero menos esperado al cierre." />
                    <MetricCard label="Recibido Cash Office" value={closureSummary.receivedCashOffice == null ? "-" : toMoney(closureSummary.receivedCashOffice)} tooltip="Monto recibido por Cash Office." />
                    <MetricCard label="Pendiente por recibir" value={toMoney(closureSummary.pendingReceipt)} tooltip="Monto contado o esperado que aun no ha sido recibido por Cash Office." />
                    <MetricCard label="Estado" value={closureSummary.status} tooltip="Cuadrado, con diferencia o pendiente segun el cierre y recepcion." />
                  </Stack>

                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Ventas
                    </Typography>
                    {loadingSales ? (
                      <Typography variant="body2" color="text.secondary">
                        Cargando ventas de la sesion...
                      </Typography>
                    ) : sessionSales.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No hay ventas vinculadas a esta sesion.
                      </Typography>
                    ) : (
                      <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Ticket</TableCell>
                              <TableCell>Cliente</TableCell>
                              <TableCell>Tipo venta</TableCell>
                              <TableCell>Pago</TableCell>
                              <TableCell align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sessionSales.map((sale) => (
                              <TableRow key={sale.id}>
                                <TableCell>{sale.sale_number || `#${sale.id}`}</TableCell>
                                <TableCell>{sale.customer_display || "-"}</TableCell>
                                <TableCell>{getSaleTypeLabel(sale)}</TableCell>
                                <TableCell>{sale.primary_payment_label || "-"}</TableCell>
                                <TableCell align="right">{toMoney(sale.total)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </Box>

                  {paymentMethodEntries.length > 0 ? (
                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                      {paymentMethodEntries.map(([method, amount]) => (
                        <Chip key={method} variant="outlined" label={`${method}: ${toMoney(amount)}`} />
                      ))}
                    </Stack>
                  ) : null}

                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Movimientos
                    </Typography>
                    <CashSessionMovementsTable
                      movements={selectedMovements}
                      variant="admin"
                      canView
                      onViewClick={setMovementDetail}
                      getMovementTypeLabel={(movement) => getMovementTypeLabel(movement, sessionSales)}
                      emptyMessage="No hay movimientos para esta sesion."
                    />
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No se pudo resolver el detalle de la sesion seleccionada.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Stack>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
      <MovementDetailDialog
        open={Boolean(movementDetail)}
        onClose={() => setMovementDetail(null)}
        movement={movementDetail}
        sale={movementDetailSale}
      />
    </Box>
  );
}

export default CashSessionCajeroPage;
